import { Fragment, FC, ReactNode, useRef, useState, useEffect, useCallback } from 'react';
import { Menu, Transition } from '@headlessui/react';
import { createPortal } from 'react-dom';
import { ChevronDown, ChevronUp } from '../../assets/icons';
import { classNames } from '../../utils';

export interface DropdownItem {
  id: string;
  value: string;
  content: ReactNode;
  text?: string;
}

export interface DropdownPropType {
  value?: string;
  placeholder?: string;
  onItemSelect?: (item: DropdownItem) => void;
  items?: DropdownItem[];
  className?: string;
  iconClassName?: string;
  style?: Record<string, unknown>;
  valueText?: string;
  header?: ReactNode;
  footer?: ReactNode;
  itemClassName?: string;
  onOpen?: () => void;
  children?: ReactNode;
  containerClassName?: string;
  itemsContainerClassName?: string;
  itemsWrapperClassName?: string;
  autoClose?: boolean;
  open?: boolean;
  isDisabled?: boolean;
  valueTemplate?: ReactNode;
  itemWrapperStyle?: Record<string, unknown>;
  onClose?: () => void;
  disabledActiveState?: boolean;
  usePortal?: boolean; // Render dropdown items in a portal to escape overflow containers
}

interface DropdownPosition {
  top: number;
  right: number;
  width: number;
}

const Dropdown: FC<DropdownPropType> = ({
  onItemSelect,
  items,
  className,
  value,
  placeholder,
  iconClassName,
  style,
  valueText,
  valueTemplate,
  header,
  footer,
  usePortal = false,
  containerClassName,
  itemClassName,
  itemsContainerClassName,
  itemsWrapperClassName,
  onOpen,
  children,
  open,
  isDisabled = false,
  autoClose = true,
  itemWrapperStyle,
  onClose,
  disabledActiveState,
}) => {
  const buttonRef = useRef<HTMLButtonElement>(null);
  const [position, setPosition] = useState<DropdownPosition>({ top: 0, right: 0, width: 0 });
  const [isOpen, setIsOpen] = useState(false);

  const updatePosition = useCallback(() => {
    if (buttonRef.current && usePortal) {
      const rect = buttonRef.current.getBoundingClientRect();
      const viewportWidth = document.documentElement.clientWidth;

      setPosition({
        top: rect.bottom + window.scrollY + 8, // 8px margin
        // Anchor to button's right edge so dropdown extends leftward (stays in viewport)
        right: Math.max(8, viewportWidth - rect.right),
        width: rect.width,
      });
    }
  }, [usePortal]);

  useEffect(() => {
    if (isOpen && usePortal) {
      updatePosition();
      window.addEventListener('scroll', updatePosition, true);
      window.addEventListener('resize', updatePosition);
      return () => {
        window.removeEventListener('scroll', updatePosition, true);
        window.removeEventListener('resize', updatePosition);
      };
    }
  }, [isOpen, usePortal, updatePosition]);

  const handleOpen = () => {
    setIsOpen(true);
    updatePosition();
    if (onOpen && items && items.length > 0) onOpen();
  };

  const handleClose = () => {
    setIsOpen(false);
    onClose?.();
  };

  const renderMenuItems = () => (
    <Menu.Items
      style={{
        ...(itemWrapperStyle || {}),
        ...(usePortal
          ? { top: position.top, right: position.right, minWidth: position.width, maxWidth: 'calc(100vw - 16px)' }
          : {}),
      }}
      {...(!autoClose && { static: true })}
      className={classNames(
        usePortal ? 'fixed z-[9999]' : 'absolute z-50 right-0',
        'mt-2 origin-top-right rounded-xl bg-white dark:bg-slate-800 shadow-xl dark:shadow-slate-950/60 ring-1 ring-gray-200/80 dark:ring-slate-700/80 backdrop-blur-sm focus:outline-none',
        itemsWrapperClassName || '',
      )}
    >
      {header && <div className="py-1">{header}</div>}
      {items && (
        <div className={`py-1 ${itemsContainerClassName || ''}`.trim()}>
          {items &&
            items.length > 0 &&
            items.map((item) => (
              <Menu.Item key={item.id}>
                {({ active }: { active: boolean }) => (
                  <div
                    onClick={() => {
                      if (item.value !== value && onItemSelect) onItemSelect(item);
                    }}
                    className={classNames(
                      'cursor-pointer transition-colors duration-150',
                      active && !disabledActiveState
                        ? 'bg-gray-100 dark:bg-slate-700/70 text-gray-900 dark:text-white'
                        : 'text-gray-700 dark:text-gray-200',
                      `block px-4 py-2.5 text-sm ${
                        value === item.value
                          ? 'bg-orange/10 dark:bg-teal-500/15 text-orange dark:text-teal-300 font-medium'
                          : ''
                      }`.trim(),
                      `${itemClassName || ''}`.trim(),
                    )}
                  >
                    {item.content}
                  </div>
                )}
              </Menu.Item>
            ))}
        </div>
      )}
      {footer && <div className="py-1">{footer}</div>}
    </Menu.Items>
  );

  const renderTransition = () => (
    <Transition
      as={Fragment}
      afterLeave={handleClose}
      afterEnter={() => setIsOpen(true)}
      {...(!autoClose && { show: open })}
      enter="transition ease-out duration-100"
      enterFrom="transform opacity-0 scale-95"
      enterTo="transform opacity-100 scale-100"
      leave="transition ease-in duration-75"
      leaveFrom="transform opacity-100 scale-100"
      leaveTo="transform opacity-0 scale-95"
    >
      {renderMenuItems()}
    </Transition>
  );

  return (
    <Menu as="div" className={`relative inline-block text-left ${containerClassName || ''}`.trim()}>
      <Menu.Button
        ref={buttonRef}
        onClick={handleOpen}
        style={style}
        className={classNames(
          `${children ? '' : 'btn-dropdown '}  ${isDisabled ? `btn-disabled` : ''}`,
          `${className || ''}`,
        )}
      >
        {children || (
          <>
            {valueTemplate || valueText || value || placeholder || ''}

            {!open && items && items.length > 0 && (
              <ChevronDown
                className={`-mr-1 ml-2 mt-1 text-gray-500 dark:text-gray-400 ${iconClassName || ''} ${
                  isDisabled ? 'opacity-30' : 'opacity-100'
                }`}
                aria-hidden="true"
              />
            )}
            {!isDisabled && items && items.length > 1 && open && (
              <ChevronUp
                className={`-mr-1 ml-2 mt-1 text-gray-500 dark:text-gray-400 ${iconClassName || ''}`}
                aria-hidden="true"
              />
            )}
          </>
        )}
      </Menu.Button>

      {(header !== undefined || footer !== undefined || (items && items.length > 0)) &&
        (usePortal ? createPortal(renderTransition(), document.body) : renderTransition())}
    </Menu>
  );
};

export default Dropdown;
